import { Link } from "react-router-dom";
import { ArrowUpRight, WhatsappLogo } from "@phosphor-icons/react";
import { SmartImage } from "@/components/SmartImage";
import { openWhatsApp } from "@/lib/site";
import { localizeProduct } from "@/lib/products";
import { useLang } from "@/lib/i18n";

export const ProductCard = ({ product }) => {
  const { t, lang } = useLang();
  const p = localizeProduct(product, lang);

  const enquire = () =>
    openWhatsApp(
      `Hello RK Jain,\nI am interested in your ${product.name}. Please share more details.`
    );

  // A product has a "clean" isolated image when it explicitly has a separate brochureImage
  // (its `image` is a product cutout, not the full brochure).
  const isCleanImage = product.brochureImage && product.brochureImage !== product.image;
  // Legacy brochure-as-image behaviour is kept for products without a clean cutout.
  const isBrochureImage = product.isBrochure && !isCleanImage;

  return (
    <div
      className="group relative flex flex-col overflow-hidden rounded-2xl border border-hairline bg-surface shadow-sm transition-all duration-500 hover:-translate-y-1.5 hover:border-forest/20 hover:shadow-2xl hover:shadow-forest/10"
      data-testid={`product-card-${product.slug}`}
    >
      <Link to={`/products/${product.slug}`} className="block overflow-hidden">
        <div className={`relative aspect-[4/3] overflow-hidden ${isCleanImage || isBrochureImage ? "bg-white" : "bg-champagne"}`}>
          <SmartImage
            src={product.image}
            alt={p.name}
            className={`h-full w-full transition-transform duration-700 ease-out group-hover:scale-105 ${
              isCleanImage
                ? "object-contain p-4"
                : isBrochureImage
                  ? "object-contain p-2"
                  : "object-cover group-hover:scale-110"
            }`}
          />
          <span className="absolute left-4 top-4 rounded-full bg-forest px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.15em] text-sand">
            {t(`categories.${product.category}`)}
          </span>
        </div>
      </Link>

      <div className="flex flex-1 flex-col p-6">
        <h3 className="font-heading text-xl font-bold tracking-tight text-forest">{p.name}</h3>
        <p className="mt-2 flex-1 text-sm leading-relaxed text-subtle">{p.short}</p>

        <div className="mt-6 flex items-center gap-3">
          <Link
            to={`/products/${product.slug}`}
            data-testid={`view-details-${product.slug}`}
            className="inline-flex items-center gap-1 text-sm font-semibold text-forest transition-colors hover:text-gold"
          >
            {t("common.viewDetails")} <ArrowUpRight size={16} weight="bold" />
          </Link>
          <button
            onClick={enquire}
            data-testid={`enquire-${product.slug}`}
            className="ml-auto inline-flex items-center gap-1.5 rounded-lg bg-forest px-4 py-2 text-xs font-semibold text-sand transition-colors hover:bg-emerald"
          >
            <WhatsappLogo size={15} weight="fill" /> {t("common.enquire")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProductCard;
